import {
  lockScroll,
  unlockScroll,
  prefersReducedMotion,
  supportsViewTransitions,
  isLowPowerDevice,
} from '@theme/utilities';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

const DENSITY_KEY = 'kh-density';
const GROUPS_KEY = 'kh-filter-groups';
const desktopQuery = window.matchMedia('(min-width: 990px)');
const PENDING_MS = 150;
const PRICE_MS = 400;

// Storage is looked up inside try: with site data blocked even reading window.localStorage throws
/** @param {string} key @param {'localStorage' | 'sessionStorage'} [kind] @returns {string | null} */
function read(key, kind = 'localStorage') {
  try {
    return window[kind].getItem(key);
  } catch {
    return null;
  }
}

/** @param {string} key @param {string} value @param {'localStorage' | 'sessionStorage'} [kind] */
function write(key, value, kind = 'localStorage') {
  try {
    window[kind].setItem(key, value);
  } catch {
    /* private mode: keep it for this page only */
  }
}

/** @returns {string[]} */
function storedGroups() {
  try {
    const groups = JSON.parse(read(GROUPS_KEY, 'sessionStorage') || '[]');
    return Array.isArray(groups) ? groups : [];
  } catch {
    return [];
  }
}

/** Filter groups the shopper opened (all closed by default), kept for the session across re-renders and pages. */
const openGroups = new Set(storedGroups());

const canTransition = () => supportsViewTransitions() && !prefersReducedMotion() && !isLowPowerDevice();
if (!supportsViewTransitions()) document.documentElement.classList.add('kh-no-vt');

/**
 * Collection page behaviour on top of Horizon's facets-form-component (design/updates/filters):
 * live filters, collapsible groups, removable chips, sort, grid density (remembered), filter panel / sheet,
 * «Показати ще». Filter updates are rendered here (facets.js hands them over): the section HTML is fetched
 * first, the open groups are written into it, then a View Transition wraps only the morph, so cards and chips move
 * instead of jumping and the page never freezes while waiting. Panel state lives on <html>, it survives the morph.
 */
class KhCollection extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  #renderToken = 0;
  /** @type {string | null} */
  #lastSearch = window.location.search;
  /** @type {number | undefined} */
  #priceTimer;

  connectedCallback() {
    const { signal } = this.#abort;
    const root = document.documentElement;

    const density = read(DENSITY_KEY);
    if (density === '3' || density === '4') root.dataset.khDensity = density;
    else delete root.dataset.khDensity; // «2 в ряд» removed (01.10): a stored 2 falls back to 3
    this.#syncDensityButtons();
    // Groups the shopper opened earlier show open on load without unfolding
    root.classList.add('kh-fg-static');
    this.#applyGroups(this);
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('kh-fg-static')));
    this.#syncPanelButton(this);

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    // Sort menu: close on a click outside it
    document.addEventListener(
      'click',
      (event) => {
        const sort = this.querySelector('[data-kh-sort][open]');
        if (sort && !sort.contains(/** @type {Node} */ (event.target))) this.#closeSort(sort);
      },
      { signal }
    );
    desktopQuery.addEventListener('change', () => this.#closeSheet(), { signal });
    // Back / forward after filtering: facets.js pushes the URL but nothing re-renders on history navigation
    window.addEventListener(
      'popstate',
      () => this.renderFilters(this.facets?.getAttribute('section-id') || this.dataset.sectionId || '').catch(console.error),
      { signal }
    );

    // Horizon may still morph the section on its own (history navigation): re-apply what the server cannot know.
    new MutationObserver(() => {
      this.#syncDensityButtons();
      this.#applyGroups(this);
      this.#syncPanelButton(this);
    }).observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#closeSheet(true);
  }

  /** @returns {(HTMLElement & { updateFilters?: () => void, updateFiltersByURL?: (url: string) => void }) | null} */
  get facets() {
    return this.querySelector('facets-form-component');
  }

  /**
   * Called by facets-form-component instead of its own render (see facets.js #updateSection).
   * @param {string} sectionId
   * @returns {Promise<void>}
   */
  async renderFilters(sectionId) {
    if (window.location.search === this.#lastSearch) return;
    this.#lastSearch = window.location.search;
    const token = ++this.#renderToken;
    const results = this.querySelector('.kh-plp__results');
    const pending = window.setTimeout(() => results?.classList.add('is-pending'), PENDING_MS);

    let html = '';
    try {
      html = await sectionRenderer.getSectionHTML(sectionId, true);
    } catch (error) {
      window.clearTimeout(pending);
      results?.classList.remove('is-pending');
      // Let the same URL render again (retry, or Back after a failed request)
      this.#lastSearch = null;
      throw error;
    }
    window.clearTimeout(pending);
    if (token !== this.#renderToken) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.#applyGroups(doc);
    this.#syncPanelButton(doc);
    const before = this.#snapshot();

    await this.#transition(() => morphSection(sectionId, doc.body.innerHTML));
    this.querySelector('.kh-plp__results')?.classList.remove('is-pending');
    this.#afterRender(before);
  }

  /**
   * Runs a DOM change inside a View Transition: cards named c<id> (TASK: view-transition-name per product), chips by
   * filter. Without support the change is immediate and new cards cascade in (.is-enter).
   * @param {() => unknown} change
   */
  async #transition(change) {
    this.#closeSort(this.querySelector('[data-kh-sort][open]'), true);
    // Phone sheet open: no transition — its named cards are drawn above the whole page, over the sheet, so the sheet
    // seemed to vanish for a moment on every filter tap (08.10); the catalogue under the sheet is hidden anyway
    if (!canTransition() || document.documentElement.hasAttribute('data-kh-filters-sheet')) {
      await change();
      return;
    }
    const root = document.documentElement;
    this.#nameParts();
    root.classList.add('kh-vt-plp');
    const transition = document.startViewTransition(async () => {
      await change();
      this.#nameParts();
    });
    // Skipped transitions (tab hidden, another one started) reject `ready`; the DOM change still happens
    transition.ready.catch(() => {});
    try {
      await transition.updateCallbackDone;
    } finally {
      transition.finished.finally(() => {
        root.classList.remove('kh-vt-plp');
        this.querySelectorAll('[data-kh-vt-named]').forEach((el) => {
          /** @type {HTMLElement} */ (el).style.removeProperty('view-transition-name');
          el.removeAttribute('data-kh-vt-named');
        });
      });
    }
  }

  /** Names visible cards and the chips for the transition (off-screen cards only cross-fade with the page). */
  #nameParts() {
    const height = window.innerHeight;
    this.querySelectorAll('[data-kh-grid] product-card[data-product-id]').forEach((card) => {
      const item = /** @type {HTMLElement | null} */ (card.closest('[data-kh-item]'));
      if (!item) return;
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > height + 200) return;
      item.style.viewTransitionName = `c${/** @type {HTMLElement} */ (card).dataset.productId}`;
      item.setAttribute('data-kh-vt-named', '');
    });
    this.querySelectorAll('[data-kh-chips] > [data-kh-vt]').forEach((chip) => {
      /** @type {HTMLElement} */ (chip).style.viewTransitionName = /** @type {HTMLElement} */ (chip).dataset.khVt || '';
      chip.setAttribute('data-kh-vt-named', '');
    });
  }

  /** @returns {{ badges: Map<string, string>, total: string, chips: Set<string>, items: Set<string> }} */
  #snapshot() {
    const badges = new Map();
    this.querySelectorAll('[data-kh-badge]').forEach((badge) => badges.set(this.#badgeKey(badge), badge.textContent?.trim() || ''));
    return {
      badges,
      total: this.querySelector('[data-kh-total]')?.textContent?.trim() || '',
      chips: new Set([...this.querySelectorAll('[data-kh-chips] > [data-kh-vt]')].map((el) => /** @type {HTMLElement} */ (el).dataset.khVt || '')),
      items: new Set([...this.querySelectorAll('[data-kh-grid] product-card')].map((el) => /** @type {HTMLElement} */ (el).dataset.productId || '')),
    };
  }

  /** @param {Element} badge */
  #badgeKey(badge) {
    return badge.getAttribute('data-kh-badge') || badge.closest('[data-kh-group]')?.getAttribute('data-kh-group') || '';
  }

  /**
   * Numbers that changed rise from below; without View Transitions new chips and cards animate in by class.
   * @param {{ badges: Map<string, string>, total: string, chips: Set<string>, items: Set<string> }} before
   */
  #afterRender(before) {
    const tick = (/** @type {Element | null} */ el) => {
      if (!el) return;
      el.classList.remove('kh-tick');
      void (/** @type {HTMLElement} */ (el).offsetWidth);
      el.classList.add('kh-tick');
    };
    this.querySelectorAll('[data-kh-badge]').forEach((badge) => {
      if (before.badges.get(this.#badgeKey(badge)) !== (badge.textContent?.trim() || '')) tick(badge);
    });
    const total = this.querySelector('[data-kh-total]');
    if (total && total.textContent?.trim() !== before.total) tick(total);

    if (canTransition()) return;
    this.querySelectorAll('[data-kh-chips] > [data-kh-vt]').forEach((chip) => {
      if (!before.chips.has(/** @type {HTMLElement} */ (chip).dataset.khVt || '')) chip.classList.add('is-in');
    });
    let k = 0;
    this.querySelectorAll('[data-kh-grid] > [data-kh-item]').forEach((item) => {
      const id = /** @type {HTMLElement | null} */ (item.querySelector('product-card'))?.dataset.productId || '';
      if (before.items.has(id)) return;
      /** @type {HTMLElement} */ (item).style.setProperty('--k', String(k++));
      item.classList.add('is-enter');
    });
  }

  /**
   * Open groups → markup (live DOM or the fetched section before the morph).
   * @param {ParentNode} scope
   */
  #applyGroups(scope) {
    scope.querySelectorAll('[data-kh-group]').forEach((group) => this.#setGroup(group, openGroups.has(group.getAttribute('data-kh-group'))));
  }

  /** @param {Element} group @param {boolean} open */
  #setGroup(group, open) {
    group.classList.toggle('is-open', open);
    const head = group.querySelector('[data-kh-group-toggle]');
    if (head && head.getAttribute('aria-expanded') !== String(open)) head.setAttribute('aria-expanded', String(open));
    const panel = group.querySelector('.kh-fg__panel');
    if (panel && panel.hasAttribute('inert') === open) panel.toggleAttribute('inert', !open);
  }

  /** @param {ParentNode} scope */
  #syncPanelButton(scope) {
    const open = String(document.documentElement.hasAttribute('data-kh-filters-open'));
    const button = scope.querySelector('[data-kh-filters-toggle]');
    if (button && button.getAttribute('aria-expanded') !== open) button.setAttribute('aria-expanded', open);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest('[data-kh-facets-form]')) return;
    event.stopPropagation();
    if (target.matches('[data-kh-price]')) return; // price: on input (debounced) or Enter
    if (target instanceof HTMLInputElement && target.matches('[data-kh-filter-all]')) {
      // «Усі колаборації»: unticked → leave for «Усі речі»; ticked again → clear the chosen collabs, stay here
      const param = target.dataset.khFilterAll || '';
      if (target.checked) {
        this.facets?.updateFiltersByURL?.(this.#urlWithout(window.location.pathname, param));
      } else {
        this.#leave(target.dataset.khLeave || '', param);
      }
      return;
    }

    const sort = target.closest('[data-kh-sort]');
    if (sort instanceof HTMLDetailsElement) this.#closeSort(sort, true);
    this.facets?.updateFilters?.();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const input = /** @type {HTMLElement} */ (event.target);
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-kh-price]')) return;
    const digits = input.value.replace(/\D/g, '');
    if (digits !== input.value) input.value = digits;
    window.clearTimeout(this.#priceTimer);
    this.#priceTimer = window.setTimeout(() => this.facets?.updateFilters?.(), PRICE_MS);
  };

  /**
   * Closes the sort menu after its closing animation (instant: no animation, e.g. before a View Transition).
   * @param {Element | null} sort
   * @param {boolean} [instant]
   */
  #closeSort(sort, instant = false) {
    if (!(sort instanceof HTMLDetailsElement) || !sort.open) return;
    if (instant || prefersReducedMotion()) {
      sort.classList.remove('is-closing');
      sort.open = false;
      return;
    }
    if (sort.classList.contains('is-closing')) return;
    sort.classList.add('is-closing');
    const menu = sort.querySelector('.kh-sort__menu');
    const done = () => {
      if (!sort.classList.contains('is-closing')) return;
      sort.classList.remove('is-closing');
      sort.open = false;
    };
    menu?.addEventListener('animationend', done, { once: true });
    window.setTimeout(done, 200); // hidden tab: no animationend
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const summary = /** @type {HTMLElement} */ (event.target).closest('[data-kh-sort] > summary');
    if (summary) {
      const sort = summary.parentElement;
      if (sort instanceof HTMLDetailsElement && sort.open) {
        event.preventDefault();
        this.#closeSort(sort);
      }
      return;
    }
    const target = /** @type {HTMLElement} */ (event.target).closest(
      '[data-kh-group-toggle],[data-kh-remove],[data-kh-filters-toggle],[data-kh-filters-close],[data-kh-density],[data-kh-load-more],button[data-kh-leave]'
    );
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('button[data-kh-leave]')) {
      event.preventDefault();
      this.#leave(target.dataset.khLeave || '', target.dataset.khLeaveStrip || '');
      return;
    }

    if (target.matches('[data-kh-group-toggle]')) {
      const group = target.closest('[data-kh-group]');
      const key = group?.getAttribute('data-kh-group');
      if (!group || !key) return;
      const open = !group.classList.contains('is-open');
      if (open) openGroups.add(key);
      else openGroups.delete(key);
      write(GROUPS_KEY, JSON.stringify([...openGroups]), 'sessionStorage');
      this.#setGroup(group, open);
    } else if (target.matches('[data-kh-remove]')) {
      event.preventDefault();
      const url = target.getAttribute('data-kh-remove') || /** @type {HTMLAnchorElement} */ (target).href;
      this.#removeFilter(target, url);
    } else if (target.matches('[data-kh-filters-toggle]')) {
      if (desktopQuery.matches) {
        this.#togglePanel();
      } else {
        this.#openSheet();
      }
    } else if (target.matches('[data-kh-filters-close]')) {
      this.#closeSheet();
    } else if (target.matches('[data-kh-density]')) {
      const value = target.dataset.khDensity || '3';
      write(DENSITY_KEY, value);
      // The change runs inside the View Transition callback (async): sync the pressed button there, not before it
      this.#transition(() => {
        document.documentElement.dataset.khDensity = value;
        this.#syncDensityButtons();
      });
    } else if (target.matches('[data-kh-load-more]')) {
      event.preventDefault();
      this.#loadMore(/** @type {HTMLAnchorElement} */ (target));
    }
  };

  /**
   * Current filters on another path, without one filter param (and without the page number).
   * @param {string} path
   * @param {string} param
   */
  #urlWithout(path, param) {
    const params = new URLSearchParams(window.location.search);
    params.delete(param);
    params.delete('page');
    const query = params.toString();
    return `${path}${query ? `?${query}` : ''}`;
  }

  /**
   * Leaves the collection (e.g. «Колаби» → «Усі речі») keeping the other filters; a page load, the collection changes.
   * @param {string} path
   * @param {string} param
   */
  #leave(path, param) {
    if (!path) return;
    window.location.href = this.#urlWithout(path, param);
  }

  /**
   * A chip (or «Скинути все»): render without it, then focus the neighbouring chip (or the «Фільтри» button).
   * @param {HTMLElement} trigger
   * @param {string} url
   */
  async #removeFilter(trigger, url) {
    const chip = trigger.closest('[data-kh-chips] > li');
    const index = chip ? [...(chip.parentElement?.children || [])].indexOf(chip) : -1;
    const hadFocus = trigger.contains(document.activeElement);
    this.facets?.updateFiltersByURL?.(url);
    if (!hadFocus || index < 0) return;
    // updateFiltersByURL does not return the render; wait for the chips list to change
    await new Promise((resolve) => {
      const list = this.querySelector('[data-kh-chips]');
      const observer = new MutationObserver(() => {
        observer.disconnect();
        resolve(null);
      });
      if (list) observer.observe(list, { childList: true });
      window.setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, 3000);
    });
    const chips = this.querySelectorAll('[data-kh-chips] > li button');
    const next = /** @type {HTMLElement | undefined} */ (chips[Math.min(index, chips.length - 1)]);
    (next || /** @type {HTMLElement | null} */ (this.querySelector('[data-kh-filters-toggle]')))?.focus();
  }

  #togglePanel() {
    const root = document.documentElement;
    const open = !root.hasAttribute('data-kh-filters-open');
    this.#transition(() => {
      root.toggleAttribute('data-kh-filters-open', open);
      this.#syncPanelButton(this);
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (event.key === 'Enter' && target.matches?.('[data-kh-price]')) {
      event.preventDefault();
      window.clearTimeout(this.#priceTimer);
      this.facets?.updateFilters?.();
      return;
    }
    if (event.key !== 'Escape') return;
    const sort = this.querySelector('[data-kh-sort][open]');
    if (sort instanceof HTMLDetailsElement) {
      this.#closeSort(sort);
      sort.querySelector('summary')?.focus();
    } else if (document.documentElement.hasAttribute('data-kh-filters-sheet')) {
      this.#closeSheet();
    }
  };

  #openSheet() {
    const root = document.documentElement;
    root.removeAttribute('data-kh-filters-closing');
    root.setAttribute('data-kh-filters-sheet', '');
    // The rise plays on opening only — a re-rendered sheet (after a filter tap) must not slide in again
    root.setAttribute('data-kh-filters-opening', '');
    window.setTimeout(() => root.removeAttribute('data-kh-filters-opening'), 560);
    lockScroll(this);
    requestAnimationFrame(() => this.querySelector('[data-kh-filters] [data-kh-filters-close]')?.focus());
  }

  /** @param {boolean} [instant] - no sinking (the section is leaving the page) */
  #closeSheet(instant = false) {
    const root = document.documentElement;
    if (!root.hasAttribute('data-kh-filters-sheet') || root.hasAttribute('data-kh-filters-closing')) return;
    const finish = () => {
      if (!root.hasAttribute('data-kh-filters-closing')) return;
      root.removeAttribute('data-kh-filters-closing');
      root.removeAttribute('data-kh-filters-sheet');
      unlockScroll(this);
      this.querySelector('[data-kh-filters-toggle]')?.focus();
    };
    // The sheet sinks first (kh-sheet-out, 360ms), then goes; without motion — at once
    root.setAttribute('data-kh-filters-closing', '');
    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
    const sheet = this.querySelector('[data-kh-filters]');
    sheet?.addEventListener('animationend', (event) => event.target === sheet && finish(), { once: true });
    window.setTimeout(finish, 420); // hidden tab: no animationend
  }

  #syncDensityButtons() {
    const current = document.documentElement.dataset.khDensity || '3';
    this.querySelectorAll('[data-kh-density]').forEach((button) => {
      const pressed = String(button.getAttribute('data-kh-density') === current);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
  }

  /** @param {HTMLAnchorElement} button */
  async #loadMore(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    const url = new URL(button.href);
    url.searchParams.set('section_id', this.dataset.sectionId || '');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      const grid = this.querySelector('[data-kh-grid]');
      const items = html.querySelectorAll('[data-kh-grid] > [data-kh-item]');
      const firstNew = items[0];
      grid?.append(...items);

      const shown = this.querySelector('[data-kh-shown]');
      const template = shown?.getAttribute('data-template');
      if (shown && template && grid) shown.textContent = template.replace('__count__', String(grid.children.length));

      const next = html.querySelector('[data-kh-load-more]');
      if (next instanceof HTMLAnchorElement) {
        button.href = next.href;
      } else {
        button.closest('.kh-ring')?.remove();
      }
      firstNew?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      window.location.href = button.href;
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('kh-collection')) customElements.define('kh-collection', KhCollection);
